import { FC, useEffect, useRef, useState } from 'react';

import { CheckCircleIcon } from '@heroicons/react/outline';

import { ContactEmailLink } from '../../components/ContactEmailLink';
import CouponNoticeModal from './CouponNoticeModal';
import { GiftBoxNotice, ValidPeriodNotice } from './CouponNotices';
import CouponProductCard from './CouponProductCard';
import ExchangeCautionNotice from './ExchangeCautionNotice';
import SendScheduleCard from './SendScheduleCard';

interface SubmitSuccessViewProps {
  redeemSubmitMeta: { submittedAt: Date; expectedSendAt: Date } | null;
  imageUrl: string;
  itemName: string;
  couponCode: string;
  brandName: string | null;
  selectedUnit: { name: string; image?: { url?: string } } | null;
  validPeriod?: string;
}

const SubmitSuccessView: FC<SubmitSuccessViewProps> = ({
  redeemSubmitMeta,
  imageUrl,
  itemName,
  couponCode,
  brandName,
  selectedUnit,
  validPeriod,
}) => {
  const scheduleRef = useRef<HTMLDivElement>(null);
  // 화면 진입 시마다 안내 모달을 한 번 띄운다.
  const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const timer = setTimeout(() => {
      scheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-6 text-center">
          <CheckCircleIcon className="mx-auto h-10 w-10 text-stone-500" />
          <h2 className="mt-3 text-lg font-medium text-stone-800">
            신청이 완료되었습니다
          </h2>
          <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
            {
              '입력하신 번호로 곧 전송됩니다.\n잠시만 기다려 주세요.\n\n평일 10시, 14시, 18시에 발송되며\n주말·공휴일은 14시에 발송됩니다.\n발송까지 30분~1시간 소요될 수 있습니다.'
            }
          </p>
        </div>

        {redeemSubmitMeta && (
          <div ref={scheduleRef} className="animate-highlight-pulse">
            <SendScheduleCard
              requestedAt={redeemSubmitMeta.submittedAt}
              expectedSendAt={redeemSubmitMeta.expectedSendAt}
            />
          </div>
        )}

        <div className="mt-4 space-y-3">
          <ValidPeriodNotice validPeriod={validPeriod} />
          <GiftBoxNotice />
        </div>

        <ExchangeCautionNotice className="mt-4" />

        <CouponProductCard
          imageUrl={imageUrl}
          itemName={itemName}
          couponCode={couponCode}
          brandName={brandName}
          selectedUnit={selectedUnit}
        />

        <ContactEmailLink />
      </div>

      <CouponNoticeModal
        open={isNoticeModalOpen}
        validPeriod={validPeriod}
        onClose={() => setIsNoticeModalOpen(false)}
      />
    </div>
  );
};

export default SubmitSuccessView;
